import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import SalovareLoader from '../common/SalovareLoader';
import employeePaymentService from '../../services/employeePaymentService';
import { getPayrollSourceTypeKey } from '../../utils/payrollSourceTypeUtils';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { BRAND } from '../../theme/brandTokens';

const formatAppointmentId = (appointmentId) => {
  if (!appointmentId) return '—';
  return appointmentId.slice(0, 8).toUpperCase();
};

const getMonthKey = (monthGroup) => `${monthGroup.year}-${monthGroup.month}`;

const AppointmentMonthTable = ({ monthGroup, currencyCode, t }) => (
  <TableContainer sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: BRAND.background }}>
          <TableCell>{t('employeePayments.payroll.appointmentDetails.date')}</TableCell>
          <TableCell>{t('employeePayments.payroll.appointmentDetails.customer')}</TableCell>
          <TableCell>{t('employeePayments.payroll.appointmentDetails.appointmentId')}</TableCell>
          <TableCell>{t('employeePayments.payroll.appointmentDetails.services')}</TableCell>
          <TableCell align="right">{t('employeePayments.payroll.appointmentDetails.totalPrice')}</TableCell>
          <TableCell align="right">{t('employeePayments.payroll.appointmentDetails.additionalCost')}</TableCell>
          <TableCell align="right">{t('employeePayments.payroll.appointmentDetails.employeeShare')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {monthGroup.appointments.map((appointment, index) => (
          <TableRow key={`${appointment.appointmentId || 'row'}-${index}`} hover>
            <TableCell>
              {appointment.appointmentDate
                ? formatUtcDate(`${appointment.appointmentDate}T00:00:00Z`)
                : '—'}
            </TableCell>
            <TableCell>{appointment.customerName || '—'}</TableCell>
            <TableCell>{formatAppointmentId(appointment.appointmentId)}</TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="body2">
                  {appointment.executedServices || '—'}
                </Typography>
                {(appointment.sourceTypes || []).map((sourceType) => (
                  <Chip
                    key={`${appointment.appointmentId}-${sourceType}`}
                    size="small"
                    label={t(`employeePayments.payroll.sourceTypes.${getPayrollSourceTypeKey(sourceType)}`)}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                ))}
              </Stack>
            </TableCell>
            <TableCell align="right">
              {formatCurrency(appointment.totalPrice, currencyCode)}
            </TableCell>
            <TableCell align="right">
              {Number(appointment.additionalCostAmount) > 0
                ? formatCurrency(appointment.additionalCostAmount, currencyCode)
                : '—'}
            </TableCell>
            <TableCell align="right">
              {Number(appointment.employeeAdditionalCostShareAmount) > 0
                ? formatCurrency(appointment.employeeAdditionalCostShareAmount, currencyCode)
                : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const EmployeePayrollAppointmentDetailsDialog = ({
  open,
  onClose,
  onExited,
  storeId,
  teamMemberId,
  teamMemberName,
  dateRange,
  currencyCode = DEFAULT_CURRENCY,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [details, setDetails] = useState(null);
  const [expandedMonthKey, setExpandedMonthKey] = useState(null);

  const totalAppointments = useMemo(
    () => (details?.months || []).reduce((sum, month) => sum + month.appointments.length, 0),
    [details],
  );

  const clearContent = useCallback(() => {
    setDetails(null);
    setLoadError(null);
    setLoading(false);
    setExpandedMonthKey(null);
  }, []);

  const handleClose = useCallback(() => {
    clearContent();
    onClose?.();
  }, [clearContent, onClose]);

  const handleExited = useCallback(() => {
    clearContent();
    onExited?.();
  }, [clearContent, onExited]);

  useEffect(() => {
    if (!open || !storeId || !teamMemberId || !dateRange?.from || !dateRange?.to) {
      return undefined;
    }

    let cancelled = false;

    const loadDetails = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await employeePaymentService.getTeamMemberPayrollAppointmentDetails(
          storeId,
          teamMemberId,
          dateRange,
        );
        if (!cancelled) {
          setDetails(data);
          if (data?.months?.length === 1) {
            setExpandedMonthKey(getMonthKey(data.months[0]));
          } else {
            setExpandedMonthKey(null);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load payroll appointment details:', error);
          setLoadError(t('employeePayments.payroll.appointmentDetails.error.loading'));
          setDetails(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [open, storeId, teamMemberId, dateRange, t]);

  const displayName = details?.teamMemberName || teamMemberName;
  const exitTransitionMs = totalAppointments > 100 ? 0 : 120;

  return (
    <ScrollableDialog
      open={open}
      onClose={handleClose}
      showCloseButton
      maxWidth="lg"
      TransitionProps={{
        onExited: handleExited,
        timeout: { enter: 225, exit: exitTransitionMs },
      }}
      title={t('employeePayments.payroll.appointmentDetails.title', { name: displayName })}
      subtitle={
        dateRange?.from && dateRange?.to
          ? t('employeePayments.payroll.appointmentDetails.subtitle', {
            from: formatUtcDate(`${dateRange.from}T00:00:00Z`),
            to: formatUtcDate(`${dateRange.to}T00:00:00Z`),
          })
          : undefined
      }
      titleSx={{ color: BRAND.primaryText }}
      contentSx={{ pt: 1 }}
    >
      {loading && (
        <SalovareLoader size="medium" center sx={{ py: 4 }} />
      )}

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {!loading && !loadError && !details?.months?.length && (
        <Alert severity="info">{t('employeePayments.payroll.appointmentDetails.empty')}</Alert>
      )}

      {!loading && !loadError && details?.months?.length > 0 && (
        <Stack spacing={1.5}>
          {details.months.map((monthGroup) => {
            const monthKey = getMonthKey(monthGroup);
            const isExpanded = expandedMonthKey === monthKey;

            return (
              <Accordion
                key={monthKey}
                expanded={isExpanded}
                disableGutters
                elevation={0}
                onChange={(_, expanded) => setExpandedMonthKey(expanded ? monthKey : null)}
                sx={{
                  border: `1px solid ${BRAND.line}`,
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: BRAND.accentText }} />}
                  sx={{
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
                    {t(`employeePayments.payroll.monthNames.${monthGroup.month}`)} {monthGroup.year}
                    {' · '}
                    {t('employeePayments.payroll.appointmentDetails.monthAppointmentCount', {
                      count: monthGroup.appointments.length,
                    })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1, sm: 1.5 }, pt: 0 }}>
                  {isExpanded ? (
                    <AppointmentMonthTable
                      monthGroup={monthGroup}
                      currencyCode={currencyCode}
                      t={t}
                    />
                  ) : null}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </ScrollableDialog>
  );
};

export default EmployeePayrollAppointmentDetailsDialog;
